// Engagement_Dashboard.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import Select, { SingleValue } from "react-select";
import { ThreeCircles } from "react-loader-spinner";
import { Clock, CheckCircle, HourglassSplit, Pen, ExclamationTriangle, XCircle, FileText, GraphUpArrow, InfoCircle} from "react-bootstrap-icons";

import styles from "./EngagementDashboard.module.css";
import { fetchDashboardData } from "./EngagementDashboard.service";

import {
  ALL_OPTION,
  SelectOption,
  DashboardFilters,
  buildFiltersPayload,
  buildSignatureStatusPieOptions,
  buildPartnerStackedBarOptions,
  buildOfficeStackedColumnOptions,
  buildPartnerReturnTypeStackedBarOptions,
  buildOfficeForecastAmountColumnOptions,
  buildReturnTypeSummaryComboOptions,
  buildLineOfServiceForecastAmountPieOptions,
  getCurrentTaxYearValue,
  ApprovalStatusRow,
  SignatureStatusRow,
  OfficeStatusRow,
  PartnerStatusRow,
  PartnerReturnTypeRow,
  ThemeColors,
  DashboardView,
  OfficeForecastAmountRow,
  ReturnTypeSummaryRow,
  LineOfServiceForecastAmountRow,
  formatCompactCurrency,
  formatKpiNumber,
} from "./EngagementDashboard.utils";
import { useGlobalStore } from "../../store/globalStore";
import { PurposeOption } from "../../client_onboarding/ClientsGrid/ClientsGrid.utils";


/**
 * Engagement_Dashboard
 * - Filters order (single select): Line of Service, Office, Partner, FY
 * - Backend does filtering; frontend just renders the pretty numbers.
 * - Filter payload values must be string[] OR omitted.
 */
type EngagementDashboardProps = {
  transporter?: (pill: string, options?: any) => void;
  saveParamToApi?: (parameter_name: string, value: any) => Promise<void>;
};

const Engagement_Dashboard: React.FC<EngagementDashboardProps> = ({transporter, saveParamToApi, }) => {
  const baseUri = process.env.REACT_APP_BASE_URI || "";

  const purposeOptionsFromStore: PurposeOption[] = useGlobalStore((s) => s.purposeOptions);
  const officeOptionsFromStore: Array<{ label: string; value: string }> = useGlobalStore((s) => s.officeLocationOptions);
  const partnerOptionsFromStore: Array<{ label: string; value: string }> = useGlobalStore((s) => s.primaryPartnerOptions);
  const myOfficeFromUserProfile: string | null = null; // user profile -> office label/value
  // ----------------------------

  const toSelectOptions = (items: any[]): SelectOption[] => {
    const mapped = items.map((x) => ({ label: x.label, value: x.value }));
    // Always include All at top
    return [ALL_OPTION, ...mapped.filter((o) => o.value !== "All")];
  };

  const lineOfServiceOptions = useMemo(
    () => toSelectOptions(purposeOptionsFromStore),
    [purposeOptionsFromStore]
  );

  const officeOptions = useMemo(
    () => toSelectOptions(officeOptionsFromStore),
    [officeOptionsFromStore]
  );


  const fyOptions: SelectOption[] = useMemo(() => {
    // If you already have FY options from backend/store, use that.
    // Otherwise keep it simple: All + current FY only (expand later).
    const current = getCurrentTaxYearValue();
    return [ALL_OPTION, { label: current, value: current }];
  }, []);

  const findOption = (options: SelectOption[], value?: string | null): SelectOption => {
    if (!value) return ALL_OPTION;
    return options.find((o) => o.value === value) || { label: value, value };
  };

  const [dashboardView, setDashboardView] = useState<DashboardView>("Operations");

  // Default filters:
  // - Office = My office
  // - FY = current tax year
  const [selectedLOS, setSelectedLOS] = useState<SelectOption>(ALL_OPTION);
  const [selectedOffice, setSelectedOffice] = useState<SelectOption>(ALL_OPTION);
  const [selectedPartner, setSelectedPartner] = useState<SelectOption>(ALL_OPTION);
  const [selectedFY, setSelectedFY] = useState<SelectOption>(ALL_OPTION);
  const [filtersReady, setFiltersReady] = useState(false);

  const partnerOptions = useMemo(() => {
    const selectedOfficeValue = selectedOffice?.value;

    if (!selectedOfficeValue || selectedOfficeValue === "All") {
      return toSelectOptions(partnerOptionsFromStore);
    }

    const filteredPartners = partnerOptionsFromStore.filter((partner: any) => {
      const partnerOffice =
        partner.location ||
        partner.office ||
        partner.Office ||
        partner.officeLocation ||
        "";

      return String(partnerOffice).trim() === String(selectedOfficeValue).trim();
    });

    return [ALL_OPTION, ...filteredPartners.map((x) => ({ label: x.label, value: x.value }))];
  }, [partnerOptionsFromStore, selectedOffice]);

  useEffect(() => {
    const officeDefault = myOfficeFromUserProfile
      ? findOption(officeOptions, myOfficeFromUserProfile)
      : ALL_OPTION;

    const fyDefault = findOption(fyOptions, getCurrentTaxYearValue());

    setSelectedLOS(ALL_OPTION);
    setSelectedOffice(officeDefault);
    setSelectedPartner(ALL_OPTION);
    setSelectedFY(fyDefault);

    setFiltersReady(true);
  }, []);

  useEffect(() => {
    const selectedOfficeValue = selectedOffice?.value;

    if (!selectedOfficeValue || selectedOfficeValue === "All") {
      return;
    }

    const partnerStillValid = partnerOptions.some(
      (option) => option.value === selectedPartner?.value
    );

    if (!partnerStillValid) {
      setSelectedPartner(ALL_OPTION);
    }
  }, [selectedOffice, partnerOptions, selectedPartner]);

  // Build backend payload using your utils contract (arrays or omit).
  const filtersForApi: DashboardFilters = useMemo(() => {
    const toArrayOrUndefined = (opt: SelectOption): string[] | undefined =>
      opt?.value && opt.value !== "All" ? [opt.value] : undefined;

    return {
      businessUnits: toArrayOrUndefined(selectedLOS),
      offices: toArrayOrUndefined(selectedOffice),
      partners: toArrayOrUndefined(selectedPartner),
      fiscalYears: toArrayOrUndefined(selectedFY), // include only if backend supports it
    };
  }, [selectedLOS, selectedOffice, selectedPartner, selectedFY]);


  const payload = useMemo(() => {
  const built = buildFiltersPayload(filtersForApi);
  return built;
}, [filtersForApi]);


  // Data
  const [loading, setLoading] = useState(true);
  const [navigating, setNavigating] = useState(false);
  const latestRequestRef = useRef(0);
  const [approvalRows, setApprovalRows] = useState<ApprovalStatusRow[]>([]);
  const [signatureRows, setSignatureRows] = useState<SignatureStatusRow[]>([]);
  const [officeRows, setOfficeRows] = useState<OfficeStatusRow[]>([]);
  const [partnerRows, setPartnerRows] = useState<PartnerStatusRow[]>([]);
  const [partnerReturnTypeRows, setPartnerReturnTypeRows] = useState<PartnerReturnTypeRow[]>([]);
  const [officeForecastAmountRows, setOfficeForecastAmountRows] = useState<OfficeForecastAmountRow[]>([]);
  const [returnTypeSummaryRows, setReturnTypeSummaryRows] = useState<ReturnTypeSummaryRow[]>([]);
  const [lineOfServiceForecastAmountRows, setLineOfServiceForecastAmountRows] = useState<LineOfServiceForecastAmountRow[]>([]);

  useEffect(() => {
    if (!filtersReady) return;

    const run = async () => {
      const requestId = ++latestRequestRef.current;
      setLoading(true);

      try {
        const res = await fetchDashboardData(baseUri, payload);

        if (requestId !== latestRequestRef.current) return;

        const noData =
          res.approvalStatus.length === 0 &&
          res.signatureStatus.length === 0 &&
          res.office.length === 0 &&
          res.primaryPartner.length === 0;

        if (noData) {
          setApprovalRows([]);
          setSignatureRows([]);
          setOfficeRows([]);
          setPartnerRows([]);
          setPartnerReturnTypeRows([]);
          setOfficeForecastAmountRows([]);
          setReturnTypeSummaryRows([]);
          setLineOfServiceForecastAmountRows([]);

          await new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve());
          });

          if (requestId === latestRequestRef.current) {
            setLoading(false);
          }
          return;
        }

        setApprovalRows(
          res.approvalStatus.map((r: any) => ({
            ApprovalStatus: r.ApprovalStatus,
            TotalCount: r.TotalCount ?? r.COUNT ?? 0,
          }))
        );

        setSignatureRows(
          res.signatureStatus.map((r: any) => ({
            SignatureStatus: r.SignatureStatus,
            TotalCount: r.TotalCount ?? r.COUNT ?? 0,
          }))
        );

        setOfficeRows(
          res.office.map((r: any) => ({
            Office: r.Office,
            ApprovalStatus: r.ApprovalStatus,
            TotalCount: r.TotalCount ?? r.COUNT ?? 0,
          }))
        );

        setPartnerRows(
          res.primaryPartner.map((r: any) => ({
            PrimaryPartner: r.PrimaryPartner,
            ApprovalStatus: r.ApprovalStatus,
            TotalCount: r.TotalCount ?? r.COUNT ?? 0,
          }))
        );

        setPartnerReturnTypeRows(
          (res.primaryPartnerReturnType || []).map((r: any) => ({
            PrimaryPartner: r.PrimaryPartner,
            ReturnType: r.ReturnType,
            TotalCount: r.TotalCount ?? r.COUNT ?? r.Count ?? 0,
            ForecastAmount: r.ForecastAmount ?? 0,
            TotalPrimaryPartnerCount: r.TotalPrimaryPartnerCount ?? 0,
            TotalPrimaryPartnerAmount: r.TotalPrimaryPartnerAmount ?? 0,
          }))
        );

        setOfficeForecastAmountRows(
          (res.officeForecastAmount || []).map((r: any) => ({
            Office: r.Office || "No Office",
            ReturnType: r.ReturnType || "No Return Type",
            ForecastAmount: r.ForecastAmount ?? r.TotalAmount ?? 0,
            ELCount: r.ELCount ?? r.TotalCount ?? 0,
            TotalOfficeCount: r.TotalOfficeCount ?? 0,
            TotalOfficeAmount: r.TotalOfficeAmount ?? 0,
          }))
        );

        setReturnTypeSummaryRows(
          (res.returnTypeSummary || []).map((r: any) => ({
            ReturnType: r.ReturnType,
            TotalCount: r.TotalCount ?? r.COUNT ?? r.Count ?? 0,
            ForecastAmount: r.ForecastAmount ?? 0,
          }))
        );

        setLineOfServiceForecastAmountRows(
          (res.lineOfServiceForecastAmount || []).map((r: any) => ({
            LineOfService: r.LineOfService,
            ForecastAmount: r.ForecastAmount ?? r.TotalAmount ?? 0,
            ELCount: r.ELCount ?? r.TotalCount ?? 0,
          }))
        );

        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        });

        if (requestId === latestRequestRef.current) {
          setLoading(false);
        }
      } catch (e) {
        if (requestId !== latestRequestRef.current) return;

        console.error(e);
        setApprovalRows([]);
        setSignatureRows([]);
        setOfficeRows([]);
        setPartnerRows([]);
        setPartnerReturnTypeRows([]);
        setOfficeForecastAmountRows([]);
        setReturnTypeSummaryRows([]);
        setLineOfServiceForecastAmountRows([]);

        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        });

        if (requestId === latestRequestRef.current) {
          setLoading(false);
        }
      }
    };

    run();
  }, [
    baseUri,
    payload,
    selectedLOS.value,
    selectedOffice.value,
    selectedPartner.value,
    selectedFY.value,
  ]);


  // Theme (optional) - only if you want to pass colors to partner chart scrollbar etc.
  const theme: ThemeColors = useMemo(() => {
    const root = document.documentElement;
    const css = getComputedStyle(root);

    let palette: string[] = [];
    try {
      const rawTheme = localStorage.getItem("chart-theme");
      const parsedTheme = rawTheme ? JSON.parse(rawTheme) : null;
      palette = Array.isArray(parsedTheme?.colors) ? parsedTheme.colors : [];
    } catch (e) {
      palette = [];
    }

    return {
      primary: css.getPropertyValue("--theme1_primary").trim() || "#1976D2",
      tertiary: css.getPropertyValue("--theme1_tertiary").trim() || "#E3F2FD",
      palette,
    };
  }, []);

  // Statuses for two dashboard views
  const OPERATIONS_STATUSES = [
    "Draft",
    "Pending Approval",
    "Partially Approved",
    "Rejected",
  ] as const;

  const OPERATIONS_SIGNATURE_STATUSES = [
    "Out For Signature",
    "Delivery Failed",
    "Partially Signed",
    "Processing",
    "Recalled",
    "Awaiting WetSign",
    "None"
  ] as const;

  const PROCESSED_SIGNATURE_STATUSES = [
  "Out For Signature",
  "Partially Signed",
  "Awaiting WetSign",
  "Processing",
  "E-Signed",
  "Signed",
  "WetSign Completed",
  "Manually Signed",
] as const;

// Charts from your utils

const executiveSignatureRows = useMemo(
    () =>
      signatureRows.filter((r) =>
        PROCESSED_SIGNATURE_STATUSES.includes(
          String(r.SignatureStatus || "").trim() as any
        )
      ),
    [signatureRows]
  );

const operationsSignatureBundle = useMemo(
  () =>
    buildSignatureStatusPieOptions(
      signatureRows,
      OPERATIONS_SIGNATURE_STATUSES
    ),
  [signatureRows]
);

const processedSignatureBundle = useMemo(
  () =>
    buildSignatureStatusPieOptions(
      executiveSignatureRows
    ),
  [executiveSignatureRows]
);

// Operations charts
  const operationsPartnerOptions = useMemo(
    () =>
      buildPartnerStackedBarOptions(
        partnerRows,
        theme,
        OPERATIONS_STATUSES
      ),
    [partnerRows, theme, OPERATIONS_STATUSES]
  );

  const operationsSignatureOptions = useMemo(
    () => operationsSignatureBundle.options,
    [operationsSignatureBundle]
  );

  const operationsOfficeOptions = useMemo(
    () =>
      buildOfficeStackedColumnOptions(
        officeRows,
        OPERATIONS_STATUSES
      ),
    [officeRows, OPERATIONS_STATUSES]
  );

  // Executive charts
  const executivePartnerOptions = useMemo(
    () =>
      buildPartnerReturnTypeStackedBarOptions(
        partnerReturnTypeRows,
        theme,
      ),
    [partnerReturnTypeRows, theme]
  );

  const executiveOfficeOptions = useMemo(
    () =>
      buildOfficeForecastAmountColumnOptions(
        officeForecastAmountRows,
        theme
      ),
    [officeForecastAmountRows, theme]
  );

  const returnTypeSummaryOptions = useMemo(
    () => buildReturnTypeSummaryComboOptions(
      returnTypeSummaryRows, 
      theme),
    [returnTypeSummaryRows, theme]
  );

  const executiveLosForecastPieOptions = useMemo(
    () => buildLineOfServiceForecastAmountPieOptions(
      lineOfServiceForecastAmountRows,
      theme
    ),
    [lineOfServiceForecastAmountRows, theme]
  );

  // Totals for KPI cards
  const operationsTotalELs = useMemo(
    () => operationsSignatureBundle.total,
    [operationsSignatureBundle]
  );


  const totalForecastAmount = useMemo(() => {
    return officeForecastAmountRows.reduce(
      (sum, row) => sum + Number(row.ForecastAmount ?? 0),
      0
    );
  }, [officeForecastAmountRows]);

  // KPIs
  const totalELs =dashboardView === "Operations" ? operationsTotalELs : processedSignatureBundle.total;

  const getApprovalCount = (statusName: string) =>
    approvalRows.find(
      (r) => String(r.ApprovalStatus || "").trim().toLowerCase() === statusName.trim().toLowerCase()
    )?.TotalCount ?? 0;

  const getSignatureCount = (statusName: string) =>
    signatureRows.find(
      (r) => String(r.SignatureStatus || "").trim().toLowerCase() === statusName.trim().toLowerCase()
    )?.TotalCount ?? 0;

  const draftCount = getApprovalCount("Draft");
  const pendingApprovalCount = getApprovalCount("Pending Approval");
  const rejectedCount = getApprovalCount("Rejected");
  const outForSignatureCount = getSignatureCount("Out For Signature");
  const deliveryFailedCount = getSignatureCount("Delivery Failed");

  const AVG_MANUAL_TIME = 25;

  const approvedCount = useMemo(() => {
    return executiveSignatureRows.reduce(
      (sum, r) => sum + (r.TotalCount ?? 0),
      0
    );
  }, [executiveSignatureRows]);

  // We no longer know actualTime, so assume full manual time saved
  const totalHoursSaved = (approvedCount * AVG_MANUAL_TIME)/60; // convert to hours


  const buildKpiGridPayload = (
    kpiType:
      | "draft"
      | "pendingApproval"
      | "outForSignature"
      | "deliveryFailed"
      | "rejected"
      | "totalProcessed"
  ) => {
    const gridModel: Record<string, any> = {};
    const serverFilter: Record<string, any> = {};

    // hard reset all known dashboard-driven filters first
    gridModel.approvalStatus = null;
    gridModel.signatureStatus = null;
    gridModel.primaryPartner = null;
    gridModel.office = null;
    gridModel.businessUnit = null;
    gridModel.fiscalYear = null;

    // include dashboard dropdown filters
    if (selectedLOS?.value && selectedLOS.value !== "All") {
      gridModel.businessUnit = {
        values: [selectedLOS.value],
        filterType: "set",
      };
      serverFilter.BusinessUnit = [selectedLOS.value];
    }

    if (selectedOffice?.value && selectedOffice.value !== "All") {
      gridModel.officeLocation = {
        values: [selectedOffice.value],
        filterType: "set",
      };
      serverFilter.OfficeLocation = [selectedOffice.value];
    }

    if (selectedPartner?.value && selectedPartner.value !== "All") {
      gridModel.primaryPartner = {
        values: [selectedPartner.value],
        filterType: "set",
      };
      serverFilter.PrimaryPartner = [selectedPartner.value];
    }

    if (kpiType === "draft") {
      gridModel.approvalStatus = {
        values: ["Draft"],
        filterType: "set",
      };
      serverFilter.ApprovalStatus = ["Draft"];
    }

    if (kpiType === "pendingApproval") {
      gridModel.approvalStatus = {
        values: ["Pending Approval"],
        filterType: "set",
      };
      serverFilter.ApprovalStatus = ["Pending Approval"];
    }

    if (kpiType === "outForSignature") {
      gridModel.signatureStatus = {
        values: ["Out For Signature"],
        filterType: "set",
      };
      serverFilter.SignatureStatus = ["Out For Signature"];
    }

    if (kpiType === "deliveryFailed") {
      gridModel.signatureStatus = {
        values: ["Delivery Failed"],
        filterType: "set",
      };
      serverFilter.SignatureStatus = ["Delivery Failed"];
    }

    if (kpiType === "rejected") {
      gridModel.approvalStatus = {
        values: ["Rejected"],
        filterType: "set",
      };
      serverFilter.ApprovalStatus = ["Rejected"];
    }

    if (kpiType === "totalProcessed") {
      gridModel.signatureStatus = {
        values: [...PROCESSED_SIGNATURE_STATUSES],
        filterType: "set",
      };
      serverFilter.SignatureStatus = [...PROCESSED_SIGNATURE_STATUSES];
    }

    return {
      gridKey: "EngagementLetterDetailsPage.grid",
      savedAt: new Date().toISOString(),
      viewMode: "myView",
      gridModel,
      serverFilter,
    };
  };

  const buildElDashboardNavUrl = (payload: any) => {
    const encodedPayload = encodeURIComponent(btoa(JSON.stringify(payload)));
    return `${window.location.origin}/dashboard?tab=client_details&kpiPayload=${encodedPayload}`;
  };

  const handleKpiClick = (
    kpiType:
      | "draft"
      | "pendingApproval"
      | "outForSignature"
      | "deliveryFailed"
      | "rejected"
      | "totalProcessed",
    event?: React.MouseEvent
  ) => {
    const payloadToSave = buildKpiGridPayload(kpiType);
    const targetUrl = buildElDashboardNavUrl(payloadToSave);

    const isNewTabAction =
      !!event && (event.button === 1 || event.metaKey || event.ctrlKey);

    if (isNewTabAction) {
      window.open(targetUrl, "_blank", "noopener,noreferrer");
      return;
    }

    setNavigating(true);
    window.location.href = targetUrl;
  };

  const getKpiHref = (
    kpiType:
      | "draft"
      | "pendingApproval"
      | "outForSignature"
      | "deliveryFailed"
      | "rejected"
      | "totalProcessed"
  ) => {
    return buildElDashboardNavUrl(buildKpiGridPayload(kpiType));
  };

  return (
    <>
      {(loading || navigating) && (
        <div className={styles.creatorLoader}>
          <ThreeCircles
            height="50"
            width="50"
            color="#4fa94d"
            visible={true}
            ariaLabel="three-circles-rotating"
            outerCircleColor="var(--theme1_tertiary)"
            innerCircleColor="var(--theme1_secondary)"
            middleCircleColor="var(--theme1_primary)"
          />
          <div className={styles.loaderText}>
            {navigating ? "Taking to EL Page" : "Loading dashboard data"}
          </div>
        </div>
      )}

      <div className={styles.page}>
        <Row className={styles.headerRow}>
          <Col md={6}>
            <p className={styles.title}>Engagement Letter Dashboard</p>
          </Col>

          <Col md={6} className={styles.toggleCol}>
            <div className={styles.dashboardToggle}>
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="toggle-tooltip-operations" className={styles.toggleTooltip}>
                    Shows EL operation insights
                  </Tooltip>
                }
              >
                <span>
                  <button
                    type="button"
                    className={`${styles.toggleBtn} ${
                      dashboardView === "Operations" ? styles.activeToggle : ""
                    }`}
                    onClick={() => setDashboardView("Operations")}
                  >
                    Operations
                  </button>
                </span>
              </OverlayTrigger>

              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="toggle-tooltip-executive" className={styles.toggleTooltip}>
                    Shows processed ELs & revenue insights
                  </Tooltip>
                }
              >
                <span>
                  <button
                    type="button"
                    className={`${styles.toggleBtn} ${
                      dashboardView === "Executive" ? styles.activeToggle : ""
                    }`}
                    onClick={() => setDashboardView("Executive")}
                  >
                    Executive
                  </button>
                </span>
              </OverlayTrigger>
            </div>
          </Col>
        </Row>

        {
          // empty ? (
          //   <div className={styles.emptyState}>
          //     <p>No dashboard data available for the selected filters.</p>
          //   </div>
          // ) : 
          (
            <>
              {/* Filters: LOS, Office, Partner, FY */}
              <Row className="mb-4">
                <Col>
                  <label className={styles.filterLabel}>Line of Service</label>
                  <Select
                    value={selectedLOS}
                    onChange={(e: SingleValue<SelectOption>) => e && setSelectedLOS(e)}
                    options={lineOfServiceOptions}
                    classNamePrefix="react-select"
                  />
                </Col>

                <Col>
                  <label className={styles.filterLabel}>Office</label>
                  <Select
                    value={selectedOffice}
                    onChange={(e: SingleValue<SelectOption>) => e && setSelectedOffice(e)}
                    options={officeOptions}
                    classNamePrefix="react-select"
                  />
                </Col>

                <Col>
                  <label className={styles.filterLabel}>Partner</label>
                  <Select
                    value={selectedPartner}
                    onChange={(e: SingleValue<SelectOption>) => e && setSelectedPartner(e)}
                    options={partnerOptions}
                    classNamePrefix="react-select"
                  />
                </Col>

                <Col>
                  <label className={styles.filterLabel}>FY</label>
                  <Select
                    value={selectedFY}
                    onChange={(e: SingleValue<SelectOption>) => e && setSelectedFY(e)}
                    options={fyOptions}
                    classNamePrefix="react-select"
                  />
                </Col>

              </Row>

              {/* KPI Banner */}
              {dashboardView === "Operations" ? (
              <Row className={`${styles.kpiBanner} mb-4`}>
                <Col
                  className={`${styles.kpiCol} ${styles.clickableKpi} d-flex align-items-center justify-content-center`}
                >
                <a
                  href={getKpiHref("draft")}
                  className={styles.kpiLink}
                  onClick={() => setNavigating(true)}
                >
                  <FileText size={37} className={styles.kpiIcon} />
                  <div className={styles.kpiTextBlock}>
                    <div className={styles.kpiLabelRow}>
                      <small className={styles.kpiSubLarge}>Draft ELs</small>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="kpi-tooltip-draft" className={styles.kpiTooltip}>
                            Go to Engagement Letters with this filter
                          </Tooltip>
                        }
                      >
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <InfoCircle size={14} className={styles.kpiInfoIcon} />
                        </span>
                      </OverlayTrigger>
                    </div>
                    <h4 className={styles.kpiValue}>{formatKpiNumber(draftCount)}</h4>
                  </div>
                </a>
              </Col>

                <Col
                  className={`${styles.kpiColRight} ${styles.clickableKpi} d-flex align-items-center justify-content-center mb-3 mb-md-0`}
                >
                <a
                  href={getKpiHref("pendingApproval")}
                  className={styles.kpiLink}
                  onClick={() => setNavigating(true)}
                >
                  <HourglassSplit size={37} className={styles.kpiIcon} style={{ marginRight: "12px" }} />
                  <div className={styles.kpiTextBlock}>
                    <div className={styles.kpiLabelRow}>
                      <small className={styles.kpiSubLarge}>Pending Approval ELs</small>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="kpi-tooltip-pending-approval" className={styles.kpiTooltip}>
                            Go to Engagement Letters with this filter
                          </Tooltip>
                        }
                      >
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <InfoCircle size={14} className={styles.kpiInfoIcon} />
                        </span>
                      </OverlayTrigger>
                    </div>
                    <h4 className={styles.kpiValue}>{formatKpiNumber(pendingApprovalCount)}</h4>
                  </div>
                  </a>
                </Col>

                <Col
                  className={`${styles.kpiColRight} ${styles.clickableKpi} d-flex align-items-center justify-content-center mb-3 mb-md-0`}
                >
                <a
                  href={getKpiHref("outForSignature")}
                  className={styles.kpiLink}
                  onClick={() => setNavigating(true)}
                >
                  <Pen size={37} className={styles.kpiIcon} style={{ marginRight: "14px" }} />
                  <div className={styles.kpiTextBlock}>
                    <div className={styles.kpiLabelRow}>
                      <small className={styles.kpiSubLarge}>Out for Signature ELs</small>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="kpi-tooltip-out-for-signature" className={styles.kpiTooltip}>
                            Go to Engagement Letters with this filter
                          </Tooltip>
                        }
                      >
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <InfoCircle size={14} className={styles.kpiInfoIcon} />
                        </span>
                      </OverlayTrigger>
                    </div>
                    <h4 className={styles.kpiValue}>{formatKpiNumber(outForSignatureCount)}</h4>
                  </div>
                  </a>
                </Col>

                <Col
                  className={`${styles.kpiColRight} ${styles.clickableKpi} d-flex align-items-center justify-content-center mb-3 mb-md-0`}
                >
                <a
                  href={getKpiHref("deliveryFailed")}
                  className={styles.kpiLink}
                  onClick={() => setNavigating(true)}
                >
                  <ExclamationTriangle size={37} className={styles.kpiIcon} style={{ marginRight: "14px" }} />
                  <div className={styles.kpiTextBlock}>
                    <div className={styles.kpiLabelRow}>
                      <small className={styles.kpiSubLarge}>Delivery Failed ELs</small>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="kpi-tooltip-delivery-failed" className={styles.kpiTooltip}>
                            Go to Engagement Letters with this filter
                          </Tooltip>
                        }
                      >
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <InfoCircle size={14} className={styles.kpiInfoIcon} />
                        </span>
                      </OverlayTrigger>
                    </div>
                    <h4 className={styles.kpiValue}>{formatKpiNumber(deliveryFailedCount)}</h4>
                  </div>
                  </a>
                </Col>

                <Col
                  className={`${styles.kpiColRight} ${styles.clickableKpi} d-flex align-items-center justify-content-center mb-3 mb-md-0`}
                >
                <a
                  href={getKpiHref("rejected")}
                  className={styles.kpiLink}
                  onClick={() => setNavigating(true)}
                >
                  <XCircle size={37} className={styles.kpiIcon} style={{ marginRight: "14px" }} />
                  <div className={styles.kpiTextBlock}>
                    <div className={styles.kpiLabelRow}>
                      <small className={styles.kpiSubLarge}>Rejected ELs</small>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="kpi-tooltip-rejected" className={styles.kpiTooltip}>
                            Go to Engagement Letters with this filter
                          </Tooltip>
                        }
                      >
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <InfoCircle size={14} className={styles.kpiInfoIcon} />
                        </span>
                      </OverlayTrigger>
                    </div>
                    <h4 className={styles.kpiValue}>{formatKpiNumber(rejectedCount)}</h4>
                  </div>
                  </a>
                </Col>
              </Row>
            ) : (
              <Row className={`${styles.kpiBanner} mb-4`}>
                <Col
                  className={`${styles.kpiCol} ${styles.clickableKpi} d-flex align-items-center justify-content-center mb-3 mb-md-0`}
                >
                <a
                  href={getKpiHref("totalProcessed")}
                  className={styles.kpiLink}
                  onClick={() => setNavigating(true)}
                >
                  <CheckCircle size={37} className={styles.kpiIcon} />
                  <div className={styles.kpiTextBlock}>
                    <div className={styles.kpiLabelRow}>
                      <small className={styles.kpiSubLarge}>Total ELs Processed</small>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="kpi-tooltip-total-processed" className={styles.kpiTooltip}>
                            Go to Engagement Letters with this filter
                          </Tooltip>
                        }
                      >
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <InfoCircle size={14} className={styles.kpiInfoIcon} />
                        </span>
                      </OverlayTrigger>
                    </div>
                    <h4 className={styles.kpiValue}>{formatKpiNumber(totalELs)}</h4>
                  </div>
                  </a>
                </Col>

                <Col
                  className={`${styles.kpiColRight} d-flex align-items-center justify-content-center mb-3 mb-md-0`}
                >
                  <Clock size={37} className={styles.kpiIcon} style={{ marginRight: "14px" }} />
                  <div className={styles.kpiTextBlock}>
                    <div className={styles.kpiLabelRow}>
                      <small className={styles.kpiSubLarge}>Total Hours Saved</small>

                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="kpi-tooltip-hours-saved" className={styles.kpiTooltip}>
                            Avg. manual time per EL = 25 mins
                          </Tooltip>
                        }
                      >
                        <span>
                          <InfoCircle size={14} className={styles.kpiInfoIcon} />
                        </span>
                      </OverlayTrigger>
                    </div>

                    <h4 className={styles.kpiValue}>
                      {formatKpiNumber(totalHoursSaved)}
                    </h4>
                  </div>
                </Col>

                <Col
                  className={`${styles.kpiColRight} d-flex align-items-center justify-content-center`}
                >
                  <GraphUpArrow size={37} className={styles.kpiIcon} style={{ marginRight: "14px" }} />
                  <div className={styles.kpiTextBlock}>
                    <div className={styles.kpiLabelRow}>
                      <small className={styles.kpiSubLarge}>Total Expected Revenue</small>
                    </div>
                    <h4 className={styles.kpiValue}>
                      ${formatCompactCurrency(totalForecastAmount)}
                    </h4>
                  </div>
                </Col>
              </Row>
            )}

              {/* Charts */}
              {dashboardView === "Operations" ? (
              <Row className="justify-content-center text-center">
                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact key="operations-partner"
                      highcharts={Highcharts}
                      options={operationsPartnerOptions}
                      immutable={true} />
                  </div>
                </Col>

                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact key="operations-office"
                      highcharts={Highcharts}
                      options={operationsOfficeOptions}
                      immutable={true}/>
                  </div>
                </Col>

                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact key="operations-signature"
                      highcharts={Highcharts}
                      options={operationsSignatureOptions}
                      immutable={true} />
                    <div className={styles.totalFooter}>
                      <strong>Total:</strong> {totalELs}
                    </div>
                  </div>
                </Col>
              </Row>
              ) : (
                <Row className="justify-content-center text-center">

                {/* <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact key="progress-signature"
                      highcharts={Highcharts}
                      options={processedSignatureBundle.options}
                      immutable={true} />
                    <div className={styles.totalFooter}>
                      <strong>Total:</strong> {processedSignatureBundle.total}
                    </div>
                  </div>
                </Col> */}

                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact
                      key="progress-los-forecast"
                      highcharts={Highcharts}
                      options={executiveLosForecastPieOptions}
                      immutable={true}
                    />
                  </div>
                </Col>

                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact key="progress-return-type-summary"
                      highcharts={Highcharts}
                      options={returnTypeSummaryOptions}
                      immutable={true} />
                  </div>
                </Col>

                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact  key="progress-office"
                      highcharts={Highcharts}
                      options={executiveOfficeOptions}
                      immutable={true} />
                  </div>
                </Col>

                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact key="progress-partner"
                      highcharts={Highcharts}
                      options={executivePartnerOptions}
                       />
                  </div>
                </Col>
 
              </Row>
              )}
            </>
          )}
      </div>
    </>
  );
};

export default Engagement_Dashboard;
